import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useParams } from "react-router-dom";

import CompellonApi from "apis/compellon";
import { EmptyState } from "components/Common";
import { LOCAL_DATASET } from "components/DataConsumption/Datasets/constants";
import useInterval from "hooks/useInterval";

import PredictJobCreate from "./JobCreate";
import PredictJobList from "./JobList";
import Prediction from "./Prediction";

import { JOB_STATUS_TYPE } from "../../../constants";
import { getOutputTypes } from "../../../utils";

const Predict = ({ jobDetails }) => {
  const [isListLoading, setIsListLoading] = useState(true);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isResultsLoading, setIsResultsLoading] = useState(false);

  const [predictionJobList, setPredictionJobList] = useState();
  const [predictionJob, setPredictionJob] = useState(null);
  const [predictionResults, setPredictionResults] = useState(null);

  const { datasetName } = useParams();

  const payloadStub = useMemo(
    () => ({
      job_id: jobDetails?.id?.toString(),
      data_location: LOCAL_DATASET(datasetName ? datasetName : "renal_failure"),
    }),
    [datasetName, jobDetails]
  );

  const fetchPredictionJobList = useCallback(async job_id => {
    setIsListLoading(true);
    try {
      const params = { job_id, job_type: ["binary_model_predict"] };
      const { data } = await CompellonApi.fetchSubJobs(params);
      setPredictionJobList(data.job_list);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsListLoading(false);
    }
  }, []);

  const createPredictionJob = useCallback(async () => {
    setIsCreateLoading(true);
    try {
      const payload = {
        ...payloadStub,
        name: `PredictTest: ${Date.now().toLocaleString()}`,
      };
      const prediction = await CompellonApi.createPrediction(payload);
      const details = await CompellonApi.fetchJobInfo({
        job_id: prediction.data.job_id,
      });
      setPredictionJob(details.data.job);
      fetchPredictionJobList(jobDetails.id);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsCreateLoading(false);
    }
  }, [jobDetails, payloadStub]);

  const fetchPredictionResults = useCallback(async job_id => {
    setIsResultsLoading(true);
    try {
      const { data } = await CompellonApi.fetchJobResult({ job_id });
      setPredictionResults(getOutputTypes(data.outputs));
    } catch (error) {
      logger.error(error);
    } finally {
      setIsResultsLoading(false);
    }
  }, []);

  const pollPredictionJob = async job_id => {
    try {
      const {
        data: { job },
      } = await CompellonApi.fetchJobInfo({ job_id });
      if (job.status !== predictionJob.status) {
        setPredictionJob(job);
        fetchPredictionResults(job.id);
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handleJobSelect = selectedJob => {
    setPredictionJob(selectedJob);
    fetchPredictionResults(selectedJob.id);
  };

  useInterval(() => {
    if (jobDetails && predictionJob?.status === JOB_STATUS_TYPE.running) {
      pollPredictionJob(predictionJob.id);
    }
  }, 2500);

  useEffect(
    () => jobDetails && fetchPredictionJobList(jobDetails.id),
    [jobDetails]
  );

  return (
    <>
      <div className="flex justify-between gap-2 mb-6">
        <PredictJobList
          jobList={predictionJobList}
          isLoading={isListLoading}
          handleJobSelect={handleJobSelect}
        />
        <PredictJobCreate
          jobDetails={jobDetails}
          predictionJob={predictionJob}
          createPredictionJob={createPredictionJob}
          isLoading={isCreateLoading}
        />
      </div>

      {predictionJob ? (
        <Prediction
          isLoading={isResultsLoading}
          results={predictionResults}
          setResults={setPredictionResults}
        />
      ) : (
        <EmptyState mainText="No Prediction Job selected" />
      )}
    </>
  );
};

export default Predict;
